/**
 * TenderListPane — pane 1 of the FORGE comms cockpit. A WhatsApp-style list of
 * in-sourcing tenders; selecting one drives the rest of the cockpit. Rows can be
 * archived (soft, restorable) to declutter; a "Show archived" toggle reveals them
 * greyed with a Restore action.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, Plus, Archive, ArchiveRestore, Users } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { statusMeta } from '../sourcingStatus';

const STAGE = {
  draft: { label: 'Draft', color: '#8b5cf6' },
  preparing: { label: 'Preparing…', color: '#8b5cf6' },
  spec: { label: 'Spec', color: '#6b7785' },
  sourcing: { label: 'Sourcing', color: '#2563eb' },
  quotes: { label: 'Quotes', color: '#059669' },
  awarded: { label: 'Awarded', color: '#15803d' },
};
const IN_SOURCING = ['preparing', 'spec', 'sourcing', 'quotes'];

const TenderListPane = ({ selectedId, onSelect, reloadKey = 0, onNew, onSuppliers }) => {
  const [tenders, setTenders] = useState(null);
  const [err, setErr] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let on = true;
    forgeAPI.listTenders()
      .then((data) => {
        const list = (Array.isArray(data) ? data : data?.tenders) || [];
        if (on) setTenders(list);
      })
      .catch(() => on && setErr('Could not load tenders'));
    return () => { on = false; };
  }, [reloadKey]);

  const setArchived = async (id, archived) => {
    setBusyId(id);
    // Optimistic: flip the flag locally so the row moves in/out of the archived group.
    setTenders((prev) => (prev || []).map((t) => (t.tender_id === id ? { ...t, archived } : t)));
    try {
      await (archived ? forgeAPI.archiveTender(id) : forgeAPI.unarchiveTender(id));
    } catch {
      setTenders((prev) => (prev || []).map((t) => (t.tender_id === id ? { ...t, archived: !archived } : t)));
    } finally {
      setBusyId(null);
    }
  };

  const all = tenders || [];
  const active = all.filter((t) => !t.archived);
  const archived = all.filter((t) => t.archived);
  const drafts = active.filter((t) => t.stage === 'draft');
  const inSourcing = active.filter((t) => IN_SOURCING.includes(t.stage) || !t.stage);
  const awarded = active.filter((t) => t.stage === 'awarded');

  const renderRow = (t) => {
    const st = t.sourcing_status ? statusMeta(t.sourcing_status) : (STAGE[t.stage] || STAGE.spec);
    const sel = t.tender_id === selectedId;
    const busy = busyId === t.tender_id;
    return (
      <button key={t.tender_id}
        className={`forge-cc-item ${sel ? 'sel' : ''} ${t.archived ? 'forge-cc-archived' : ''}`}
        style={{ borderLeftColor: sel ? st.color : 'transparent' }}
        onClick={() => onSelect(t.tender_id)}>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title || t.tender_id}</span>
          <span className="forge-muted" style={{ fontSize: 11, display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="forge-cc-dot" style={{ background: st.color }} />
            {st.label} · {(t.line_items ?? 0)} item{(t.line_items === 1) ? '' : 's'}
            {t.candidates ? ` · ${t.candidates} sup` : ''}
            {t.quotes ? ` · ${t.quotes} quote${t.quotes === 1 ? '' : 's'}` : ''}
          </span>
        </span>
        {t.archived ? (
          <span className="forge-ck-rowbtn" title="Restore to active list"
            onClick={(e) => { e.stopPropagation(); if (!busy) setArchived(t.tender_id, false); }}>
            {busy ? <Loader2 size={13} className="forge-spin" /> : <ArchiveRestore size={13} />}
          </span>
        ) : (
          <>
            <span className="forge-ck-rowbtn forge-ck-rowbtn-hover" title="Archive"
              onClick={(e) => { e.stopPropagation(); if (!busy) setArchived(t.tender_id, true); }}>
              {busy ? <Loader2 size={13} className="forge-spin" /> : <Archive size={13} />}
            </span>
            <Link to={`/forge/${encodeURIComponent(t.tender_id)}`} title="Open full workspace (spec / bid requirements)"
              onClick={(e) => e.stopPropagation()} className="forge-ck-open">
              <ExternalLink size={13} />
            </Link>
          </>
        )}
      </button>
    );
  };

  return (
    <div className="forge-cc-pane forge-cc-scroll forge-ck-tenders">
      {(onNew || onSuppliers) && (
        <div className="forge-ck-tenders-hd">
          {onNew && (
            <button className="forge-ck-newproduct" onClick={onNew}
              title="Design your own product and source it">
              <Plus size={14} /> New product
            </button>
          )}
          {/* Cross-tender supplier registry. It lived only in the retired
              workspace shell, which nothing linked to, so the console was
              effectively unreachable — surfaced here rather than deleted. */}
          {onSuppliers && (
            <button className="forge-btn-ghost forge-ck-suppliers-btn" onClick={onSuppliers}
              title="Supplier console — registry, favourites, run history">
              <Users size={14} /> Suppliers
            </button>
          )}
        </div>
      )}
      {drafts.length > 0 && (
        <>
          <div className="forge-cc-grouphdr">Drafts · {drafts.length}</div>
          {drafts.map(renderRow)}
          <div style={{ height: 8 }} />
        </>
      )}
      <div className="forge-cc-grouphdr">In sourcing · {inSourcing.length}</div>
      {tenders === null && <div className="forge-muted" style={{ padding: 14 }}><Loader2 size={14} className="forge-spin" /> Loading…</div>}
      {err && <div className="forge-muted" style={{ padding: 14, color: '#b91c1c' }}>{err}</div>}
      {tenders !== null && inSourcing.length === 0 && (
        <div className="forge-muted" style={{ padding: 14, fontSize: 13 }}>No tenders in sourcing yet.</div>
      )}
      {inSourcing.map(renderRow)}

      {awarded.length > 0 && (
        <>
          <div className="forge-cc-grouphdr" style={{ marginTop: 12 }}>Awarded · {awarded.length}</div>
          <div className="forge-ck-awarded">{awarded.map(renderRow)}</div>
        </>
      )}

      {archived.length > 0 && (
        <>
          <button className="forge-ck-archtoggle" onClick={() => setShowArchived((v) => !v)}>
            <Archive size={12} /> {showArchived ? 'Hide' : 'Show'} archived · {archived.length}
          </button>
          {showArchived && <div className="forge-ck-awarded">{archived.map(renderRow)}</div>}
        </>
      )}
    </div>
  );
};

export default TenderListPane;
